import React from 'react';
import { useTranslation } from 'react-i18next';
import metricsService from '@/core/services/MetricsService';
import { WarningBanner } from '@/core/components/WarningBanner/WarningBanner';
import { getAccountId, getUniId, getUserId } from '@/core/utils/extractCookieId';
import './MetricsUsageBanner.scss';

type MetricsUsageBannerLevel = 'none' | 'warning' | 'critical';
const WARNING_THRESHOLD = 70;
const CRITICAL_THRESHOLD = 100;
const DISMISS_TTL_MS = 12 * 60 * 60 * 1000;
const DISMISS_STORAGE_PREFIX = 'mfe:metrics-usage-banner:dismiss-until';
const DEFAULT_DISMISS_STORAGE_KEY = `${DISMISS_STORAGE_PREFIX}:global`;

export interface MetricsUsageBannerProps {
  onVisibilityChange?: (isVisible: boolean) => void;
}

const resolveListeningPercentage = (limits?: {
  month_posts_limit?: number | null;
  total_posts_limit?: number | null;
  total_month_listening_posts?: number | null;
  total_listening_posts?: number | null;
}): number => {
  if (!limits) {
    return 0;
  }

  const monthLimit = limits.month_posts_limit ?? 0;
  const totalLimit = limits.total_posts_limit ?? 0;
  const shouldUseMonthlyMetrics = monthLimit > 0;
  const listeningValue = shouldUseMonthlyMetrics
    ? (limits.total_month_listening_posts ?? 0)
    : (limits.total_listening_posts ?? 0);
  const limit = shouldUseMonthlyMetrics ? monthLimit : totalLimit;

  if (limit <= 0) {
    return 0;
  }

  return (listeningValue / limit) * 100;
};

const getBannerLevel = (
  percentage: number,
): MetricsUsageBannerLevel => {
  if (percentage >= CRITICAL_THRESHOLD) {
    return 'critical';
  }

  if (percentage >= WARNING_THRESHOLD) {
    return 'warning';
  }

  return 'none';
};

const buildDismissStorageKey = (): string => {
  const accountId = getAccountId()?.trim();
  const universeId = getUniId()?.trim();
  const userId = getUserId()?.trim();

  if (accountId && universeId && userId) {
    return `${DISMISS_STORAGE_PREFIX}:${accountId}:${universeId}:${userId}`;
  }

  return DEFAULT_DISMISS_STORAGE_KEY;
};

const isDismissed = (storageKey: string): boolean => {
  try {
    const dismissedUntilValue = localStorage.getItem(storageKey);

    if (!dismissedUntilValue) {
      return false;
    }

    const dismissedUntil = Number(dismissedUntilValue);

    if (Number.isNaN(dismissedUntil)) {
      localStorage.removeItem(storageKey);
      return false;
    }

    if (Date.now() >= dismissedUntil) {
      localStorage.removeItem(storageKey);
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const persistDismiss = (storageKey: string): void => {
  try {
    localStorage.setItem(storageKey, String(Date.now() + DISMISS_TTL_MS));
  } catch {
    // Ignore storage errors so banner behavior still works without persistence.
  }
};

export const MetricsUsageBanner: React.FC<MetricsUsageBannerProps> = ({ onVisibilityChange }) => {
  const { t } = useTranslation();
  const [level, setLevel] = React.useState<MetricsUsageBannerLevel>('none');
  const dismissStorageKeyRef = React.useRef(DEFAULT_DISMISS_STORAGE_KEY);

  const handleClose = React.useCallback(() => {
    persistDismiss(dismissStorageKeyRef.current);
    setLevel('none');
  }, []);

  React.useEffect(() => {
    let isMounted = true;

    const loadMetrics = async () => {
      try {
        const response = await metricsService.getUniverseLimits();
        const percentage = resolveListeningPercentage(response?.limits);
        const resolvedLevel = getBannerLevel(percentage);
        const dismissStorageKey = buildDismissStorageKey();

        if (!isMounted) {
          return;
        }

        dismissStorageKeyRef.current = dismissStorageKey;

        if (resolvedLevel !== 'none' && isDismissed(dismissStorageKey)) {
          setLevel('none');
          return;
        }

        setLevel(resolvedLevel);
      } catch {
        if (!isMounted) {
          return;
        }

        setLevel('none');
      }
    };

    void loadMetrics();

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    onVisibilityChange?.(level !== 'none');
  }, [level, onVisibilityChange]);

  if (level === 'none') {
    return null;
  }

  const message = level === 'critical'
    ? t('metricsUsageBanner.criticalMessage')
    : t('metricsUsageBanner.warningMessage');
  const variant = level === 'critical' ? 'error' : 'warning';

  return (
    <WarningBanner
      className="metrics-usage-banner"
      messageClassName="metrics-usage-banner__text"
      message={message}
      link={t('metricsUsageBanner.linkUrl')}
      linkText={t('metricsUsageBanner.linkText')}
      variant={variant}
      context="outside"
      close
      linkTarget="_blank"
      onClose={handleClose}
    />
  );
};
